import React from 'react';
import styled from 'styled-components';

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #dee2e6;
  
  @media (max-width: 768px) {
    gap: 0.375rem;
    padding: 0.75rem;
  }
`;

const CalendarHeader = styled.div`
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  padding: 0.5rem 0;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
    padding: 0.375rem 0;
  }
`;

const CalendarDate = styled.button<{ 
  isScheduled?: boolean; 
  isSelected?: boolean; 
  isDisabled?: boolean;
  isPast?: boolean;
}>`
  aspect-ratio: 1;
  border: 2px solid ${props => {
    if (props.isSelected) return '#000000';
    if (props.isScheduled) return '#000000';
    return '#dee2e6';
  }};
  background: ${props => {
    if (props.isSelected) return '#000000';
    if (props.isScheduled) return '#ffffff';
    if (props.isPast) return '#f8f9fa';
    return '#ffffff';
  }};
  color: ${props => {
    if (props.isSelected) return '#ffffff';
    if (props.isPast) return '#adb5bd';
    return '#000000';
  }};
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: ${props => props.isSelected ? '700' : props.isScheduled ? '600' : '500'};
  cursor: ${props => props.isPast ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:hover:not(:disabled) {
    ${props => !props.isPast && `
      background: ${props.isScheduled ? '#f8f9fa' : '#e9ecef'};
      transform: scale(1.05);
    `}
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    border-width: 1.5px;
  }
`;

const CalendarMonth = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
  color: #000000;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const MonthButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  color: #000000;
  
  &:hover {
    opacity: 0.7;
  }
`;

const ScheduleDot = styled.div`
  position: absolute;
  bottom: 3px;
  width: 4px;
  height: 4px;
  background: #000000;
  border-radius: 50%;
`;

interface CalendarProps {
  year: number;
  month: number;
  scheduledDates: string[];
  selectedDate?: string;
  onDateClick?: (dateStr: string) => void;
  onMonthChange?: (year: number, month: number) => void;
  readOnly?: boolean;
  allowPastDates?: boolean; // 과거 날짜 선택 허용 여부
}

const Calendar: React.FC<CalendarProps> = ({
  year,
  month,
  scheduledDates,
  selectedDate,
  onDateClick,
  onMonthChange,
  readOnly = false,
  allowPastDates = false
}) => {
  const generateCalendar = () => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const calendar = [];
    
    for (let i = 0; i < startDayOfWeek; i++) {
      calendar.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const currentDate = new Date(year, month - 1, day);
      currentDate.setHours(0, 0, 0, 0);
      const isPast = currentDate < today;
      
      calendar.push({
        day,
        dateStr,
        isScheduled: scheduledDates.includes(dateStr),
        isPast: isPast
      });
    }
    
    return calendar;
  };

  const calendar = generateCalendar();
  
  const handlePrevMonth = () => {
    if (onMonthChange) {
      if (month === 1) {
        onMonthChange(year - 1, 12);
      } else {
        onMonthChange(year, month - 1);
      }
    }
  };
  
  const handleNextMonth = () => {
    if (onMonthChange) {
      if (month === 12) {
        onMonthChange(year + 1, 1);
      } else {
        onMonthChange(year, month + 1);
      }
    }
  };

  return (
    <CalendarGrid>
      <CalendarMonth>
        <MonthButton onClick={handlePrevMonth}>◀</MonthButton>
        <span>{year}년 {month}월</span>
        <MonthButton onClick={handleNextMonth}>▶</MonthButton>
      </CalendarMonth>
      
      {['일', '월', '화', '수', '목', '금', '토'].map(day => (
        <CalendarHeader key={day}>{day}</CalendarHeader>
      ))}
      
      {calendar.map((date, index) => {
        if (!date) {
          return <div key={`empty-${index}`} />;
        }
        
        return (
          <CalendarDate
            key={date.dateStr}
            type="button"
            isScheduled={date.isScheduled}
            isSelected={selectedDate === date.dateStr}
            isPast={date.isPast && !allowPastDates}
            disabled={readOnly ? false : (date.isPast && !allowPastDates)}
            onClick={(e) => {
              e.preventDefault();
              // allowPastDates가 false이고 과거 날짜면 클릭 불가
              if (date.isPast && !allowPastDates) {
                return;
              }
              // 과거 날짜가 아니면 모든 날짜 클릭 가능 (일정 있으면 수정, 없으면 추가)
              if (onDateClick) {
                onDateClick(date.dateStr);
              }
            }}
          >
            {date.day}
            {date.isScheduled && <ScheduleDot />}
          </CalendarDate>
        );
      })}
    </CalendarGrid>
  );
};

export default Calendar;

